import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Layers, Plus, Search, Filter, Eye, Edit2, Trash2, Copy, ExternalLink,
  Loader2, Code, BarChart3, Globe, FileText, MousePointer, Check
} from 'lucide-react';
import { landingPageAPI } from '../api';
import { format } from 'date-fns';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-700',
};

export const LandingPagesListPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, published: 0, totalViews: 0, totalSubmissions: 0 });
  
  // Embed dialog state
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [selectedPageForEmbed, setSelectedPageForEmbed] = useState(null);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  useEffect(() => {
    fetchPages();
  }, [search, statusFilter]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const res = await landingPageAPI.getAll(params);
      const pagesList = res.data?.pages || res.data || [];
      setPages(pagesList);
      
      // Calculate stats
      const published = pagesList.filter(p => p.status === 'published').length;
      const totalViews = pagesList.reduce((acc, p) => acc + (p.views || 0), 0);
      const totalSubmissions = pagesList.reduce((acc, p) => acc + (p.submissions || 0), 0);
      setStats({
        total: pagesList.length,
        published,
        totalViews,
        totalSubmissions,
      });
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      toast.error('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this landing page?')) return;
    
    try {
      await landingPageAPI.delete(id);
      toast.success('Landing page deleted');
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete landing page');
    }
  };

  const handleDuplicate = async (page) => {
    try {
      const newPage = {
        ...page,
        name: `${page.name} (Copy)`,
        slug: `${page.slug}-copy`,
        status: 'draft',
      };
      delete newPage.id;
      delete newPage.created_at;
      delete newPage.updated_at;
      delete newPage.views;
      delete newPage.submissions;
      
      const res = await landingPageAPI.create(newPage);
      toast.success('Landing page duplicated');
      navigate(`/marketing/landing-pages/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to duplicate landing page');
    }
  };

  const getPublicUrl = (page) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${baseUrl}/p/${page.slug}`;
  };

  // Get embed code for a page
  const getEmbedCode = (page) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `<script src="${baseUrl}/api/landing-pages/${page?.id}/embed.js" async></script>
<div id="landing-page-${page?.slug}"></div>`;
  };

  // Get form-only embed code
  const getFormOnlyEmbedCode = (page) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `<iframe src="${baseUrl}/p/${page?.slug}?form_only=true" 
  width="100%" 
  height="500" 
  frameborder="0"
  style="border: none; max-width: 600px;">
</iframe>`;
  };

  // Copy embed code
  const copyEmbedCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedEmbed(true);
    toast.success('Embed code copied!');
    setTimeout(() => setCopiedEmbed(false), 2000);
  };

  // Open embed dialog
  const handleOpenEmbed = (e, page) => {
    e.stopPropagation();
    setSelectedPageForEmbed(page);
    setShowEmbedDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-pages-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6" />
            Landing Pages
          </h1>
          <p className="text-muted-foreground">
            Create and manage landing pages with lead capture forms
          </p>
        </div>
        <Button onClick={() => navigate('/marketing/landing-pages/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Page
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Pages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Globe className="h-5 w-5 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{stats.published}</p>
            <p className="text-sm text-muted-foreground">Published</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Eye className="h-5 w-5 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{stats.totalViews.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Total Views</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <MousePointer className="h-5 w-5 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold">{stats.totalSubmissions.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Submissions</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search pages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pages List */}
      {pages.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Layers className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Landing Pages Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first landing page with a drag-and-drop builder
            </p>
            <Button onClick={() => navigate('/marketing/landing-pages/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Page
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(page => (
            <Card 
              key={page.id} 
              className="hover:shadow-md transition-shadow group"
              data-testid={`landing-page-${page.id}`}
            >
              {/* Preview Thumbnail */}
              <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                {page.og_image ? (
                  <img src={page.og_image} alt={page.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Layers className="h-12 w-12 text-slate-400" />
                  </div>
                )}
                <Badge 
                  className={`absolute top-2 right-2 ${STATUS_COLORS[page.status] || STATUS_COLORS.draft}`}
                >
                  {page.status}
                </Badge>
                
                {/* Quick Actions Overlay on Hover */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`/marketing/landing-pages/${page.id}`); }}
                    data-testid={`edit-page-${page.id}`}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Page
                  </Button>
                </div>
              </div>

              <CardContent className="p-4">
                <h3 className="font-semibold mb-1 truncate">{page.name}</h3>
                <p className="text-sm text-muted-foreground mb-3 truncate">
                  /p/{page.slug}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {page.views || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <MousePointer className="h-4 w-4" />
                    {page.submissions || 0}
                  </span>
                  {page.views > 0 && (
                    <span className="flex items-center gap-1">
                      <BarChart3 className="h-4 w-4" />
                      {((page.submissions / page.views) * 100).toFixed(1)}%
                    </span>
                  )}
                </div>

                {/* Primary Actions - More Explicit */}
                <div className="flex items-center gap-2 pt-3 border-t mb-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); navigate(`/marketing/landing-pages/${page.id}`); }}
                    data-testid={`edit-btn-${page.id}`}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); navigate('/marketing/landing-pages/analytics', { state: { pageId: page.id } }); }}
                    data-testid={`analytics-btn-${page.id}`}
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Analytics
                  </Button>
                </div>
                
                {/* Secondary Actions */}
                <div className="flex items-center gap-1">
                  {page.status === 'published' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          window.open(getPublicUrl(page), '_blank'); 
                        }}
                        title="View Live Page"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleOpenEmbed(e, page)}
                        title="Get Embed Code"
                        data-testid={`embed-btn-${page.id}`}
                      >
                        <Code className="h-4 w-4 mr-1" />
                        Embed
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(page); }}
                    title="Duplicate Page"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 ml-auto"
                    onClick={(e) => { e.stopPropagation(); handleDelete(page.id); }}
                    title="Delete Page"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Embed Code Dialog */}
      <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Embed {selectedPageForEmbed?.name}
            </DialogTitle>
            <DialogDescription>
              Copy the embed code to add this landing page to your website
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="full" className="mt-4">
            <TabsList className="mb-4">
              <TabsTrigger value="full">Full Page Embed</TabsTrigger>
              <TabsTrigger value="form">Form Only</TabsTrigger>
            </TabsList>
            
            <TabsContent value="full" className="space-y-4">
              <div className="relative">
                <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-sm overflow-x-auto">
                  {getEmbedCode(selectedPageForEmbed)}
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyEmbedCode(getEmbedCode(selectedPageForEmbed))}
                >
                  {copiedEmbed ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                This code will embed the full landing page including all sections.
              </p>
            </TabsContent>
            
            <TabsContent value="form" className="space-y-4">
              <div className="relative">
                <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
                  {getFormOnlyEmbedCode(selectedPageForEmbed)}
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyEmbedCode(getFormOnlyEmbedCode(selectedPageForEmbed))}
                >
                  {copiedEmbed ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                This code will embed only the lead capture form from your landing page.
              </p>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg mt-4">
            <Globe className="h-4 w-4 text-blue-600" />
            <p className="text-sm text-blue-700">
              Form submissions will automatically create contacts and leads in your CRM.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPagesListPage;
